import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe, Heart, BarChart3, Map } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useFavorites } from '../context/FavoritesContext';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const location = useLocation();
  const { language, switchLanguage, t } = useLanguage();
  const { favoritesCount, compareCount } = useFavorites();

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/woningen', label: t('nav.properties') },
    { path: '/costa-blanca', label: t('nav.costaBlanca') },
    { path: '/costa-calida', label: t('nav.costaCalida') },
    { path: '/costa-almeria', label: t('nav.costaAlmeria') },
    { path: '/investeren', label: t('nav.invest') },
    { path: '/blog', label: t('nav.blog') },
    { path: '/contact', label: t('nav.contact') },
  ];

  return (
    <header style={styles.header}>
      <div style={styles.container}>
        <Link to="/" style={styles.logo} data-testid="logo-link">
          <img src="/logo.png" alt="CasaOthers" style={styles.logoImage} />
        </Link>

        {/* Desktop Navigation */}
        {isDesktop && (
          <nav style={styles.desktopNav}>
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                style={{
                  ...styles.navLink,
                  ...(isActive(link.path) ? styles.navLinkActive : {}),
                }}
                data-testid={`nav-${link.path.replace('/', '') || 'home'}`}
              >
                {link.label}
              </Link>
            ))}

            {/* Personalisation icons — map, favorites, compare */}
            <Link to="/kaart" style={styles.iconNav} title={language === 'nl' ? 'Kaart' : 'Map'} data-testid="nav-map">
              <Map size={18} />
            </Link>
            <Link to="/favorieten" style={styles.iconNav} title={language === 'nl' ? 'Favorieten' : 'Favorites'} data-testid="nav-favorites">
              <Heart size={18} />
              {favoritesCount > 0 && <span style={styles.navBadge}>{favoritesCount}</span>}
            </Link>
            <Link to="/vergelijken" style={styles.iconNav} title={language === 'nl' ? 'Vergelijken' : 'Compare'} data-testid="nav-compare">
              <BarChart3 size={18} />
              {compareCount > 0 && <span style={styles.navBadge}>{compareCount}</span>}
            </Link>
            
            {/* Language Switcher */}
            <div style={styles.langSwitcher}>
              <button
                style={styles.langButton}
                onClick={() => setShowLangMenu(!showLangMenu)}
                data-testid="language-switcher"
              >
                <Globe size={18} />
                <span>{language.toUpperCase()}</span>
              </button>
              {showLangMenu && (
                <div style={styles.langMenu}>
                  <button
                    style={{
                      ...styles.langOption,
                      ...(language === 'nl' ? styles.langOptionActive : {}),
                    }}
                    onClick={() => {
                      switchLanguage('nl');
                      setShowLangMenu(false);
                    }}
                    data-testid="lang-nl"
                  >
                    🇳🇱 Nederlands
                  </button>
                  <button
                    style={{
                      ...styles.langOption,
                      ...(language === 'en' ? styles.langOptionActive : {}),
                    }}
                    onClick={() => {
                      switchLanguage('en');
                      setShowLangMenu(false);
                    }}
                    data-testid="lang-en"
                  >
                    🇬🇧 English
                  </button>
                  <button
                    style={{
                      ...styles.langOption,
                      ...(language === 'pl' ? styles.langOptionActive : {}),
                    }}
                    onClick={() => {
                      switchLanguage('pl');
                      setShowLangMenu(false);
                    }}
                    data-testid="lang-pl"
                  >
                    🇵🇱 Polski
                  </button>
                </div>
              )}
            </div>
          </nav>
        )}

        {/* Mobile Menu Button */}
        {!isDesktop && (
          <div style={styles.mobileControls}>
            {/* Mobile Language Switcher — cycles nl → en → pl → nl */}
            <button
              style={styles.mobileLangButton}
              onClick={() => {
                const next = language === 'nl' ? 'en' : language === 'en' ? 'pl' : 'nl';
                switchLanguage(next);
              }}
              data-testid="mobile-language-switcher"
              title={language === 'nl' ? 'Switch to English' : language === 'en' ? 'Zmień na polski' : 'Nederlands'}
            >
              {language === 'nl' ? '🇬🇧' : language === 'en' ? '🇵🇱' : '🇳🇱'}
            </button>
            <button
              style={styles.mobileMenuButton}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        )}
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <nav style={styles.mobileNav}>
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              style={{
                ...styles.mobileNavLink,
                ...(isActive(link.path) ? styles.mobileNavLinkActive : {}),
              }}
              onClick={() => setMobileMenuOpen(false)}
              data-testid={`mobile-nav-${link.path.replace('/', '') || 'home'}`}
            >
              {link.label}
            </Link>
          ))}
          <Link to="/kaart" style={styles.mobileNavLink} onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-map">
            🗺️ {language === 'nl' ? 'Kaart' : 'Map'}
          </Link>
          <Link to="/favorieten" style={styles.mobileNavLink} onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-favorites">
            ❤️ {language === 'nl' ? 'Favorieten' : 'Favorites'}{favoritesCount > 0 && ` (${favoritesCount})`}
          </Link>
          <Link to="/vergelijken" style={styles.mobileNavLink} onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-compare">
            📊 {language === 'nl' ? 'Vergelijken' : 'Compare'}{compareCount > 0 && ` (${compareCount})`}
          </Link>
        </nav>
      )}
    </header>
  );
};

const styles = {
  header: {
    backgroundColor: '#FAF9F6',
    borderBottom: '1px solid #EAE5DD',
    position: 'sticky',
    top: 0,
    zIndex: 1100,
  },
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '1.5rem 1.5rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  logo: {
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
  },
  logoImage: {
    height: '48px',
    width: 'auto',
    display: 'block',
  },
  logoText: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '1.75rem',
    color: '#1A1918',
    fontWeight: 500,
  },
  desktopNav: {
    display: 'flex',
    gap: '2rem',
    alignItems: 'center',
  },
  navLink: {
    textDecoration: 'none',
    color: '#585755',
    fontSize: '0.9rem',
    fontWeight: 400,
    letterSpacing: '0.02em',
    transition: 'color 0.3s ease',
  },
  navLinkActive: {
    color: '#C5A059',
    fontWeight: 500,
  },
  langSwitcher: {
    position: 'relative',
    marginLeft: '1rem',
  },
  langButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    backgroundColor: 'transparent',
    border: '1px solid #EAE5DD',
    padding: '0.5rem 1rem',
    color: '#585755',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: 500,
    transition: 'all 0.3s ease',
  },
  langMenu: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: '0.5rem',
    backgroundColor: 'white',
    border: '1px solid #EAE5DD',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    zIndex: 100,
    minWidth: '150px',
  },
  langOption: {
    display: 'block',
    width: '100%',
    padding: '0.75rem 1rem',
    border: 'none',
    backgroundColor: 'transparent',
    textAlign: 'left',
    cursor: 'pointer',
    fontSize: '0.9rem',
    color: '#585755',
    transition: 'background-color 0.3s ease',
  },
  langOptionActive: {
    backgroundColor: '#FAF9F6',
    color: '#C5A059',
    fontWeight: 500,
  },
  mobileControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  },
  mobileLangButton: {
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: '1.25rem',
    cursor: 'pointer',
    padding: '0.25rem',
  },
  mobileMenuButton: {
    display: 'block',
    background: 'none',
    border: 'none',
    color: '#1A1918',
    cursor: 'pointer',
  },
  mobileNav: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#FAF9F6',
    borderTop: '1px solid #EAE5DD',
    padding: '1rem 1.5rem 1.5rem',
    gap: '0.25rem',
    // Cap height so the drawer fits inside the viewport under the sticky
    // header (~65px). Anything above the fold overflows and scrolls
    // internally instead of getting clipped.
    maxHeight: 'calc(100vh - 65px)',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    overscrollBehavior: 'contain',
  },
  mobileNavLink: {
    textDecoration: 'none',
    color: '#585755',
    fontSize: '1rem',
    fontWeight: 400,
    padding: '0.9rem 0',
    borderBottom: '1px solid #EAE5DD',
    flexShrink: 0,
  },
  mobileNavLinkActive: {
    color: '#C5A059',
    fontWeight: 500,
  },
  iconNav: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '36px',
    height: '36px',
    color: '#1A1918',
    textDecoration: 'none',
    marginLeft: '0.35rem',
    transition: 'color 0.15s ease',
  },
  navBadge: {
    position: 'absolute',
    top: '-2px',
    right: '-4px',
    backgroundColor: '#C5A059',
    color: 'white',
    fontSize: '0.6rem',
    fontWeight: 700,
    padding: '0.05rem 0.3rem',
    minWidth: '16px',
    height: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '999px',
  },
};

export default Header;
